import { ChangeDetectionStrategy, Component, OnInit, computed, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { WorkflowGraphStore } from './store/workflow-graph.store';
import { WorkflowEditorService } from './store/workflow-editor.service';
import { WorkflowFlowCanvasComponent } from './components/flow-canvas/flow-canvas.component';
import { WorkflowNodePaletteComponent } from './components/node-palette/node-palette.component';
import { WorkflowInspectorHostComponent } from './components/inspector/inspector-host/inspector-host.component';
import { WorkflowValidationBarComponent } from './components/validation-bar/validation-bar.component';
import { workflowLifecycleStatusLabel } from '../models/workflow-labels';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';

/**
 * Shell FINO del builder de workflow (Fase 2). Sustituye al monolito viejo (~827 TS + 628 HTML)
 * por un orquestador: monta el `WorkflowGraphStore` + `WorkflowEditorService` (uno por workflow
 * abierto) y compone la barra superior + paleta + canvas + inspector. NADA de lógica de estado
 * vive aquí: toda está en el store; el shell solo lee sus signals y dispara acciones del editor.
 *
 * - Reactividad SOLO por signals del store/editor (gotcha §6): OnPush.
 * - readOnly (ACTIVE/ARCHIVED) lo deriva el editor y lo respetan paleta, canvas e inspector.
 * - El guardado atómico por nodeKey (PUT /builder/graph REPLACE) lo maneja el editor/store.
 */
@Component({
  selector: 'app-workflow-builder',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    WorkflowNodePaletteComponent,
    WorkflowFlowCanvasComponent,
    WorkflowInspectorHostComponent,
    WorkflowValidationBarComponent,
    HasPermissionDirective
  ],
  templateUrl: './workflow-builder.component.html',
  providers: [WorkflowGraphStore, WorkflowEditorService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowBuilderComponent implements OnInit {
  /** Estado del workflow en español: la cabecera pintaba `DRAFT` / `ARCHIVED`. */
  readonly workflowStatusText = workflowLifecycleStatusLabel;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly store = inject(WorkflowGraphStore);
  protected readonly editor = inject(WorkflowEditorService);

  /** El canvas expone `focusNode` para centrar el nodo culpable de un aviso de validación. */
  private readonly canvas = viewChild(WorkflowFlowCanvasComponent);

  protected readonly workflow = this.editor.workflow;
  protected readonly loading = this.editor.loading;
  protected readonly fatalError = this.editor.fatalError;
  protected readonly readOnly = computed(() => this.editor.readOnly());
  protected readonly readOnlyMessage = computed(() => this.editor.readOnlyMessage());
  protected readonly workflowId = computed(() => this.editor.workflowId());
  protected readonly graphErrors = this.store.graphErrors;

  /** Texto del indicador de persistencia de la barra superior. */
  protected readonly saveLabel = computed(() => {
    switch (this.store.saveState()) {
      case 'saving':
        return 'Guardando…';
      case 'saved':
        return 'Guardado';
      case 'unsaved':
        return 'Sin guardar';
      case 'error':
        return 'Error al guardar';
      default:
        return '';
    }
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id || Number.isNaN(Number(id))) {
      this.router.navigate(['/automation/workflows']);
      return;
    }
    this.editor.load(Number(id));
  }

  // ── Acciones de la barra superior ──────────────────────────────────────────

  protected save(): void {
    this.editor.save(false);
  }

  protected validate(): void {
    this.editor.validate();
  }

  protected undo(): void {
    if (this.readOnly() || !this.store.canUndo()) {
      return;
    }
    this.store.undo();
    this.editor.queueAutosave();
  }

  protected redo(): void {
    if (this.readOnly() || !this.store.canRedo()) {
      return;
    }
    this.store.redo();
    this.editor.queueAutosave();
  }

  protected retry(): void {
    const id = this.workflowId();
    if (id != null) {
      this.editor.load(id);
    }
  }

  /** Aviso de validación pulsado → centra y selecciona el nodo en el canvas. */
  protected onFocusNode(nodeKey: string): void {
    this.canvas()?.focusNode(nodeKey);
  }
}
